import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  useSEO({
    title: "Login — Maqam",
    description: "Log in to Maqam — Saudi Arabia's AI-powered job-application platform.",
    path: "/login",
  });

  useEffect(() => {
    const err = searchParams.get("google");
    const reason = searchParams.get("reason");
    if (err === "error") {
      const friendly = {
        invalid_state: "Sign-in link expired (sometimes happens with browser back-button). Please click 'Continue with Google' again.",
        state_expired: "Sign-in took too long (>10 min). Please try again.",
        missing_code: "Google didn't return an auth code. Please try again.",
        no_email: "Google didn't share your email. Please try email login.",
        exchange_failed: "Google rejected the sign-in. Please try again or use email + password.",
        redirect_uri_mismatch: "Domain mismatch — please make sure you're on https://maqam-ksa.com (no 'www') and try again.",
        invalid_grant: "Sign-in code already used or expired. Please click 'Continue with Google' again.",
        pkce_failed: "Sign-in security check failed. Please clear cookies and try again.",
        user_upsert_failed: "Account creation failed. Please contact support.",
      }[reason] || `Google sign-in failed (${reason || "unknown"}). Please try again.`;
      toast.error(friendly);
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (u.subscription === "recruiter") navigate("/recruiter/search");
      else navigate("/app/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      const status = err?.response?.status;
      if (status === 401 || /invalid/i.test(detail)) {
        toast.error("Wrong email or password. If you signed up with Google, click Continue with Google below.");
      } else if (status === 404 || /not found/i.test(detail)) {
        toast.error("No account found with this email. Try signing up instead.");
      } else if (!err?.response) {
        toast.error("Network error — please check your connection and try again.");
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#1a1a1c] text-white">
      <div className="hidden md:flex p-12 flex-col justify-between relative overflow-hidden border-r border-white/10">
        <Link to="/" className="flex items-center gap-1 w-fit">
          <span className="font-display font-black text-2xl tracking-tighter inline-flex items-center">
            <span className="border border-white px-1.5 py-0.5 mr-[2px] leading-none">M</span>
            <span>aqam</span>
          </span>
        </Link>
        <div>
          <h1 className="font-display font-normal text-5xl lg:text-6xl tracking-tight leading-[1]">
            Welcome back.
            <br />
            <span className="italic font-light text-white/90">Let's apply.</span>
          </h1>
          <p className="text-white/60 text-lg mt-6 max-w-md font-light">
            Your platform inbox is waiting with updates from employers across the Kingdom.
          </p>
        </div>
        <div className="font-mono text-xs text-white/40 uppercase tracking-widest">/ Built for Saudi Arabia</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-[#1a1a1c]">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <h2 className="font-display font-normal text-3xl md:text-4xl tracking-tight mb-2 text-white">{t("login")}</h2>
          <p className="text-white/60 mb-10">
            New here?{" "}
            <Link to="/register" className="link-underline text-white font-medium" data-testid="to-register">
              Create an account
            </Link>
          </p>

          <label className="block mb-6">
            <span className="block font-mono text-xs uppercase tracking-widest text-white/50 mb-2">{t("email")}</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b-2 border-white/20 focus:border-white outline-none py-3 text-lg text-white placeholder-white/30"
              data-testid="login-email"
            />
          </label>
          <label className="block mb-10">
            <span className="block font-mono text-xs uppercase tracking-widest text-white/50 mb-2">{t("password")}</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b-2 border-white/20 focus:border-white outline-none py-3 text-lg text-white"
              data-testid="login-password"
            />
          </label>

          <button
            disabled={loading}
            className="w-full bg-white text-[#1a1a1c] hover:bg-white/90 font-bold py-5 flex items-center justify-center gap-3 transition-colors disabled:opacity-60 rounded-full"
            data-testid="login-submit"
          >
            {loading ? "…" : t("login")} <ArrowRight className="w-5 h-5" />
          </button>

          <div className="relative my-4" style={{ display: process.env.REACT_APP_GOOGLE_AUTH_ENABLED === "true" ? undefined : "none" }}>
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#1a1a1c] px-3 text-white/40 uppercase tracking-widest">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={async () => {
              try {
                // Use the same-origin-aware api client so the OAuth flow stays
                // on whichever domain the user is on (maqam-ksa.com or preview).
                // Using raw REACT_APP_BACKEND_URL bakes in the build-time URL
                // (e.g. apply-swift-2.emergent.host) and breaks cross-domain.
                const r = await api.get("/oauth/google/login?mode=login");
                const auth_url = r.data?.auth_url;
                if (!auth_url) throw new Error("no auth_url");
                window.location.href = auth_url;
              } catch (err) {
                const status = err?.response?.status;
                if (status === 503) {
                  toast.error("Google sign-in is not configured on the server yet.");
                } else {
                  toast.error("Could not start Google sign-in. Please try email + password.");
                }
                // surface the real reason in the browser console for debugging
                // eslint-disable-next-line no-console
                console.error("Google sign-in failed:", err?.response?.data || err);
              }
            }}
            className="w-full border border-white/20 bg-white/[0.04] hover:bg-white/[0.08] text-white font-medium py-4 flex items-center justify-center gap-3 transition-colors rounded-full"
            data-testid="google-signin-btn"
            style={{ display: process.env.REACT_APP_GOOGLE_AUTH_ENABLED === "true" ? undefined : "none" }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
        </form>
      </div>
    </div>
  );
}
